// src/components/MessagesPage.tsx
import { useEffect, useState, useRef, useCallback } from "react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { pubnubService } from "../services/PubnubService";
import { SharetribeRepository } from "@/infrastructure/repositories/SharetribeRepository";
import { apiService } from "@/services";
import { pubnubChatRepository } from "@/infrastructure/repositories/PubNubChatRepository";
import { Input } from "./ui/input";
import { secureStorage } from "@/utils/secureStorage";


export function   MessagesPage({ openNewMessageSignal }: { openNewMessageSignal?: number }) {
  const repo = new SharetribeRepository(apiService);

  // ALWAYS use swimmerId (same as SharePage)
  const swimmerId = String(localStorage.getItem("current_user_swimmer_id") || "");
  const myIdString = swimmerId;

  // ================= STATES =================
  const [connectedUsers, setConnectedUsers] = useState<any[]>([]);
  const [conversations, setConversations] = useState<Record<string, any[]>>({});
  const [unreadMap, setUnreadMap] = useState<Record<string, number>>({});
  const [activeChatUser, setActiveChatUser] = useState<string | null>(null);
  const [messageText, setMessageText] = useState("");
  const [activeSet, setActiveSet] = useState<Record<string, boolean>>({});
  const suppressOlderFetchRef = useRef(false);

  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const [conversationLoading, setConversationLoading] = useState(false);

  const [showNewMessageDialog, setShowNewMessageDialog] = useState(false);
  const prevSignalRef = useRef(openNewMessageSignal);
  const userType = secureStorage.get("user_type") || "swimmer";
  const isFetchingOlderRef = useRef(false);
  const oldestTimetokenRef = useRef<Record<string, number>>({});
  const lastScrollTopRef = useRef(0);
  const userScrolledUpRef = useRef(false);
  const [pageLoading, setPageLoading] = useState(true);
  
  const mergeMessages = (oldMsgs: any[], newMsgs: any[]) => {
    const map = new Map<string, any>();

    [...oldMsgs, ...newMsgs].forEach((m) => {
      const key = m.clientId || String(m.timetoken);
      map.set(key, m);
    });

    return Array.from(map.values()).sort(
      (a, b) => Number(a.timetoken) - Number(b.timetoken)
    );
  };



  // ======================================================
  // OPEN DIALOG WHEN SHAREPAGE TRIGGERS SIGNAL
  // ======================================================
  useEffect(() => {
    if (openNewMessageSignal === undefined) return;

    if (prevSignalRef.current !== openNewMessageSignal) {
      prevSignalRef.current = openNewMessageSignal;
      setShowNewMessageDialog(true);
    }
  }, [openNewMessageSignal]);


  const initMessaging = useCallback(async () => {
  try {
    setPageLoading(true);

    pubnubService.init(myIdString);

    let list: any[] = [];

    if (userType === "coach") {
      const coachId = Number(localStorage.getItem("coach_id") || 0);
      list = await repo.getCoachSwimmers(coachId, 1);
    } else {
      const swimmerId = Number(localStorage.getItem("current_user_swimmer_id") || 0);
      list = await repo.getSwimmerCoaches(swimmerId, 1);
    }

    const cleaned = list.map((u: any) => ({
      ...u,
      id: String(u.id),
    }));

    setConnectedUsers(cleaned);

    const channels = cleaned.flatMap((u) => [
      `${myIdString}.${u.id}`,
      `${u.id}.${myIdString}`,
    ]);

    pubnubService.pubnub?.unsubscribeAll();
    if (channels.length > 0) {
      pubnubService.subscribe(channels);
    }

    pubnubService.pubnub?.removeAllListeners();
    pubnubService.addListener({
      message: handleIncomingMessage,
      presence: handlePresence,
      status: () => refreshHereNow(cleaned),
    });

    await loadLastMessages(cleaned);
    initUnread(cleaned);
  } catch (err) {
    console.error("Messaging init error:", err);
  } finally {
    setPageLoading(false);
  }
}, [myIdString, userType]);


  // ======================================================
  // INIT MESSAGING (identical to SharePage)
  // ======================================================
  useEffect(() => {
  initMessaging();
  return () => {
    pubnubService.pubnub?.unsubscribeAll();
  };
}, [initMessaging]);
  
const truncateText = (text: string, maxLength = 40) => {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + "...";
};


  // ======================================================
  // LAST MESSAGES (IDENTICAL NORMALIZATION)
  // ======================================================
  const loadLastMessages = async (list: any[]) => {
  const convs: Record<string, any[]> = {};

  for (const u of list) {
    const theirId = String(u.id);

    const ch1 = `${theirId}.${myIdString}`;
    const ch2 = `${myIdString}.${theirId}`;

    try {
      const resp = await pubnubService.pubnub?.fetchMessages({
        channels: [ch1, ch2],
        count: 1,
      });

      const arr1 = resp?.channels?.[ch1] || [];
      const arr2 = resp?.channels?.[ch2] || [];

      const combined = [...arr1, ...arr2]
        .map((m: any) => {
          const payload = typeof m.message === "object" ? m.message : {};

          return {
              id: payload?.clientId ?? m.timetoken,
            text:
              payload?.text ??
              payload?.content ??
              (typeof m.message === "string" ? m.message : ""),
            senderId:
              payload?.senderId ??
              payload?.from ??
              m.publisher ??
              "",
            timetoken: m.timetoken,
            raw: payload,
            _preview: true,
          };
        })
        .sort((a, b) => Number(b.timetoken) - Number(a.timetoken));

      convs[theirId] = combined.slice(0, 1);
    } catch {
      convs[theirId] = [];
    }
  }

  setConversations((prev) => {
    if (Object.keys(prev).length > 0) return prev;
    return convs;
  });
};




  const initUnread = (contacts: any[]) => {
    const unread: Record<string, number> = {};
    contacts.forEach((u) => (unread[u.id] = 0));
    setUnreadMap(unread);
  };

  // ======================================================
  // INCOMING MESSAGE (EXACT FROM SHAREPAGE)
  // ======================================================
 const handleIncomingMessage = (event: any) => {
  try {
    if (event.publisher === myIdString) return;

    const channel = event.channel;
    const parts = channel.split(".");
    const theirId = parts[0] === myIdString ? parts[1] : parts[0];

    const payload =
      typeof event.message === "object" ? event.message : {};

    const text =
      payload?.text ??
      payload?.content ??
      (typeof event.message === "string" ? event.message : "");

    const clientId = payload?.clientId;
    const timetoken = event.timetoken;

    const newMsg = {
      id: clientId ?? timetoken,
      text,
      senderId: payload?.senderId ?? event.publisher,
      timetoken,
      clientId,
      raw: payload,
    };

    // IMPORTANT: prevent pagination during live messages
    suppressOlderFetchRef.current = true;

    setConversations((prev) => {
      const existing = prev[theirId] || [];

      if (clientId) {
        const idx = existing.findIndex(
          (m) => m.clientId === clientId
        );
        if (idx !== -1) {
          const updated = [...existing];
          updated[idx] = newMsg;
          return { ...prev, [theirId]: updated };
        }
      }

      if (existing.some((m) => m.timetoken === timetoken)) {
        return prev;
      }

      return {
        ...prev,
        [theirId]: [...existing, newMsg],
      };
    });

    setUnreadMap((prev) => {
      if (activeChatUser === theirId) return prev;
      return { ...prev, [theirId]: (prev[theirId] || 0) + 1 };
    });

    // release lock after React flush
    requestAnimationFrame(() => {
      suppressOlderFetchRef.current = false;
      userScrolledUpRef.current = false;
      lastScrollTopRef.current =
        messagesContainerRef.current?.scrollTop ?? 0;
    });

  } catch (err) {
    console.error("Incoming message error", err);
  }
};

  // ======================================================
  // PRESENCE
  // ======================================================
  const handlePresence = (p: any) => {
    try {
      const parts = p.channel.split(".");
      const theirId = parts[0] === myIdString ? parts[1] : parts[0];

      setActiveSet((prev) => ({
        ...prev,
        [theirId]: p.action === "join",
      }));
    } catch {}
  };

  const refreshHereNow = async (list: any[]) => {
    try {
      const channels = list.map((u) => `${myIdString}.${u.id}`);

      const resp = await pubnubService.pubnub?.hereNow({
        channels,
        includeUUIDs: true,
      });

      const map: Record<string, boolean> = {};

      list.forEach((u) => {
        const ch = `${myIdString}.${u.id}`;
        map[u.id] = (resp?.channels?.[ch]?.occupants?.length ?? 0) > 0;
      });

      setActiveSet(map);
    } catch {}
  };

  const markConversationRead = (theirId: string) => {
  setUnreadMap((prev) => ({
    ...prev,
    [theirId]: 0,
  }));
};


  // ======================================================
  // OPEN CONVERSATION (IDENTICAL TO SHAREPAGE)
  // ======================================================
  const openConversation = async (theirId: string) => {
  setConversationLoading(true);

  hasMoreRef.current[theirId] = true;

  const existing = conversations[theirId] || [];
  const isPreviewOnly =
    existing.length === 1 && existing[0]?._preview;

  if (existing.length > 0 && !isPreviewOnly) {
    setActiveChatUser(theirId);
    markConversationRead(theirId);

    if (!oldestTimetokenRef.current[theirId]) {
      oldestTimetokenRef.current[theirId] = existing[0].timetoken;
    }

    setConversationLoading(false);
    requestAnimationFrame(() => scrollBottom(false));
    return;
  }

  // remove preview message
  setConversations((prev) => {
    const msgs = prev[theirId] || [];
    if (msgs.length === 1 && msgs[0]?._preview) {
      return { ...prev, [theirId]: [] };
    }
    return prev;
  });

  setActiveChatUser(theirId);
  markConversationRead(theirId);

  try {
    const pubnub = pubnubService.pubnub;
    if (!pubnub) return;

    const ch1 = `${theirId}.${myIdString}`;
    const ch2 = `${myIdString}.${theirId}`;

    const resp = await pubnub.fetchMessages({
      channels: [ch1, ch2],
      count: 30,
    });

    const arr1 = resp.channels?.[ch1] || [];
    const arr2 = resp.channels?.[ch2] || [];

    const combined = [...arr1, ...arr2]
      .map((m: any) => {
        const payload = typeof m.message === "object" ? m.message : {};
        return {
          id: payload?.clientId ?? m.timetoken,
          text:
            payload?.text ??
            payload?.content ??
            (typeof m.message === "string" ? m.message : ""),
          senderId:
            payload?.senderId ??
            payload?.from ??
            m.publisher ??
            "",
          timetoken: m.timetoken,
          clientId: payload?.clientId,
          raw: payload,
        };
      })
      .sort((a, b) => Number(a.timetoken) - Number(b.timetoken));

    if (combined.length > 0) {
      oldestTimetokenRef.current[theirId] = combined[0].timetoken;
      hasMoreRef.current[theirId] = true;
    } else {
      hasMoreRef.current[theirId] = false;
    }

    setConversations((prev) => ({
      ...prev,
      [theirId]: mergeMessages([], combined),
    }));
  } catch (err) {
    console.error("openConversation error", err);
  } finally {
    setConversationLoading(false);
    requestAnimationFrame(() => scrollBottom(false));
  }
};


const hasMoreRef = useRef<Record<string, boolean>>({});

const fetchOlderMessages = async () => {
  if (!activeChatUser) return;
  if (isFetchingOlderRef.current) return;
  if (hasMoreRef.current[activeChatUser] === false) return;

  // 🚫 block pagination during live message insert
  if (suppressOlderFetchRef.current) return;

  const beforeToken = oldestTimetokenRef.current[activeChatUser];
  if (!beforeToken) {
    hasMoreRef.current[activeChatUser] = false;
    return;
  }

  const pubnub = pubnubService.pubnub;
  if (!pubnub) return;

  isFetchingOlderRef.current = true;

  try {
    const ch1 = `${activeChatUser}.${myIdString}`;
    const ch2 = `${myIdString}.${activeChatUser}`;

    const container = messagesContainerRef.current;
    const prevScrollHeight = container?.scrollHeight || 0;

    const resp = await pubnub.fetchMessages({
      channels: [ch1, ch2],
      count: 30,
      end: String(Number(beforeToken) - 1),
    });

    const currentOldest = oldestTimetokenRef.current[activeChatUser];

  const older = [
    ...(resp.channels?.[ch1] || []),
    ...(resp.channels?.[ch2] || []),
  ]
    .filter((m: any) => {
      return Number(m.timetoken) < Number(currentOldest);
    })

      .map((m: any) => {
          const payload = typeof m.message === "object" ? m.message : {};
          return {
            id: payload?.clientId ?? m.timetoken,
            text:
              payload?.text ??
              payload?.content ??
              (typeof m.message === "string" ? m.message : ""),
            senderId:
              payload?.senderId ??
              payload?.from ??
              m.publisher ??
              "",
            timetoken: m.timetoken,
            clientId: payload?.clientId,
            raw: payload,
          };
        })
      .sort((a, b) => Number(a.timetoken) - Number(b.timetoken));

    if (older.length === 0) {
      hasMoreRef.current[activeChatUser] = false;
      return;
    }

    oldestTimetokenRef.current[activeChatUser] = older[0].timetoken;

    setConversations((prev) => ({
      ...prev,
      [activeChatUser]: mergeMessages(
        older,
        prev[activeChatUser] || []
      ),
    }));

    requestAnimationFrame(() => {
      if (!container) return;
      container.scrollTop =
        container.scrollHeight - prevScrollHeight;
    });
  } catch (err) {
    console.error("fetchOlderMessages error", err);
  } finally {
    isFetchingOlderRef.current = false;
  }
};

const deleteConversation = async () => {
  if (!activeChatUser) return;

  const confirmed = window.confirm(
    "This will delete the entire conversation for both users. Continue?"
  );
  if (!confirmed) return;

  const ch1 = `${myIdString}.${activeChatUser}`;
  const ch2 = `${activeChatUser}.${myIdString}`;

  try {
    setConversationLoading(true);

     await pubnubChatRepository.deleteAllMessages(ch1);
    await pubnubChatRepository.deleteAllMessages(ch2);

    // Clear local state
    setConversations((prev) => ({
      ...prev,
      [activeChatUser]: [],
    }));

    oldestTimetokenRef.current[activeChatUser] = 0;
    setUnreadMap((prev) => ({ ...prev, [activeChatUser]: 0 }));

    // Exit chat view
    setActiveChatUser(null);
  } catch (err) {
    console.error("Delete conversation failed", err);
  } finally {
    setConversationLoading(false);
  }
};

  const scrollBottom = (smooth = true) => {
  const el = messagesContainerRef.current;
  if (!el) return;

  el.scrollTo({
    top: el.scrollHeight,
    behavior: smooth ? "smooth" : "auto",
  });
};

useEffect(() => {
  setActiveChatUser(null);
  setConversations({});
  setUnreadMap({});
}, []);

  // ======================================================
  // SEND MESSAGE (exact SharePage logic)
  // ======================================================
  const sendMessage = async () => {
    if (!activeChatUser || !messageText.trim()) return;

    const text = messageText.trim();
    const clientId = crypto.randomUUID();

    const payload = {
      text,
      senderId: myIdString,
      clientId,
      timestamp: Date.now(),
    };

    // local push
    setConversations((prev) => {
      const updated = { ...prev };
      if (!updated[activeChatUser]) updated[activeChatUser] = [];
      updated[activeChatUser].push({
      id: clientId,
      text,
      senderId: myIdString,
      timetoken: Date.now(),
      clientId,
      raw: payload,
      });

      return updated;
    });

   userScrolledUpRef.current = false;
  setMessageText("");
  requestAnimationFrame(() => scrollBottom(true));
    requestAnimationFrame(() => scrollBottom(true));

    try {
      await pubnubService.pubnub?.publish({
        channel: `${myIdString}.${activeChatUser}`,
        message: payload,
      });
    } catch (err) {
      console.error("Publish failed", err);
    }
  };

  const recentChats = connectedUsers
  .map((u) => {
    const msgs = conversations[u.id] || [];
    const lastMsg = msgs[msgs.length - 1];

    return {
      ...u,
      _lastTimetoken: lastMsg?.timetoken
        ? Number(lastMsg.timetoken)
        : 0,
    };
  })
  .filter((u) => {
    const unread = unreadMap[u.id] || 0;
    return u._lastTimetoken > 0 || unread > 0;
  })
  .sort((a, b) => b._lastTimetoken - a._lastTimetoken);

  const activeUser = connectedUsers.find(
  (u) => u.id === activeChatUser
);

const autoOpenedRef = useRef(false);

useEffect(() => {
  if (
    !autoOpenedRef.current &&
    !activeChatUser &&
    recentChats.length > 0
  ) {
    autoOpenedRef.current = true;
    openConversation(recentChats[0].id);
  }
}, [recentChats]);

  // ======================================================
  // UI
  // ======================================================

if (pageLoading) {
  return (
    <div className="fixed inset-0 flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
    </div>
  );
}


  return (
  <div className="space-y-6">
    {/* Page Header */}
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-muted-foreground">
          Chat with your coaches and swimmers
        </p>
      </div>

      <Button onClick={() => setShowNewMessageDialog(true)}>
        + New Message
      </Button>
    </div>

    

    {/* MAIN LAYOUT */}
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">

      {/* CONTACT LIST */}
      <div className="lg:col-span-1">
  <div className="border rounded-lg overflow-hidden h-[600px] flex flex-col bg-background">

    {/* Header */}
    <div className="px-4 py-3 border-b font-semibold">
      Conversations
    </div>

    {/* List */}
    <div className="flex-1 overflow-y-auto p-2 space-y-1">
      {recentChats.length === 0 && (
        <p className="text-sm text-muted-foreground text-center mt-6">
          No conversations yet
        </p>
      )}

      {recentChats.map((u: any) => {
        const lastMsg = conversations[u.id]?.slice(-1)?.[0];
        const unread = unreadMap[u.id] || 0;
        const isActive = activeChatUser === u.id;

        return (
          <div
            key={u.id}
            onClick={() => openConversation(u.id)}
            className={`
              group flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer
              transition-colors
              ${isActive
                ? "bg-primary/10 border border-primary/30"
                : "hover:bg-muted"}
            `}
          >
            {/* Avatar */}
            <div className="relative shrink-0">
              <img
                src={u.image || u.profilePic || "/placeholder-avatar.jpg"}
                className="w-10 h-10 rounded-full object-cover"
              />

              {/* Online indicator */}
              {activeSet[u.id] && (
                <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-background rounded-full" />
              )}
            </div>

            {/* Text */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <p
                  className={`
                    text-sm truncate
                    ${unread > 0 ? "font-semibold" : "font-medium"}
                  `}
                >
                  {u.fname} {u.lname}
                </p>

                {/* Unread badge */}
                {unread > 0 && (
                  <span className="ml-2 min-w-[20px] h-5 px-1.5 text-xs flex items-center justify-center rounded-full bg-red-500 text-white">
                    {unread}
                  </span>
                )}
              </div>

              <p className="text-xs text-muted-foreground truncate">
                {lastMsg?.text
                  ? truncateText(lastMsg.text, 36)
                  : "No messages yet"}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  </div>
</div>



      {/* CHAT PANEL */}
      <div className="lg:col-span-3">
        {!activeChatUser ? (
          <div className="h-[600px] border rounded-lg flex items-center justify-center text-muted-foreground">
            Select a conversation to start chatting
          </div>
        ) : (
          <div className="h-[600px] border rounded-lg flex flex-col overflow-hidden">

            {/* CHAT HEADER */}
            <div className="shrink-0 px-4 py-3 border-b flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img
                  src={
                    activeUser?.image ||
                    activeUser?.profilePic ||
                    "/placeholder-avatar.jpg"
                  }
                  className="w-10 h-10 rounded-full object-cover"
                />

                <div>
                  <p className="font-semibold leading-tight">
                    {activeUser
                      ? `${activeUser.fname} ${activeUser.lname}`
                      : "Chat"}
                  </p>

                  {activeUser && activeSet[activeUser.id] && (
                    <p className="text-xs text-green-500">online</p>
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    markConversationRead(activeChatUser);
                    setActiveChatUser(null);
                  }}
                >
                  Back
                </Button>

                <Button
                  size="sm"
                  variant="destructive"
                  onClick={deleteConversation}
                  disabled={conversationLoading}
                >
                  Delete
                </Button>
              </div>
            </div>

            {/* MESSAGES */}
            <div
              ref={messagesContainerRef}
              className="flex-1 overflow-y-auto p-4"
              onScroll={(e) => {
                const el = e.currentTarget;

                const currentScrollTop = el.scrollTop;
                const delta = currentScrollTop - lastScrollTopRef.current;

                if (delta < -10) userScrolledUpRef.current = true;
                lastScrollTopRef.current = currentScrollTop;

                if (!userScrolledUpRef.current) return;
                if (currentScrollTop > 40) return;

                if (
                  !isFetchingOlderRef.current &&
                  hasMoreRef.current[activeChatUser] !== false &&
                  !conversationLoading &&
                  !suppressOlderFetchRef.current
                ) {
                  fetchOlderMessages();
                }
              }}
            >
              <div className="space-y-3">
                {conversationLoading ? (
                  <div className="text-center text-sm text-muted-foreground">
                    Loading messages...
                  </div>
                ) : (
                  (conversations[activeChatUser] || []).map((m: any, i: number) => {
                    const mine = m.senderId === myIdString;

                    return (
                      <div
                        key={m.id || i}
                        className={`flex ${mine ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`
                            px-3 py-2 rounded-lg text-sm max-w-[75%]
                            whitespace-pre-wrap break-words
                            ${mine
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted"}
                          `}
                        >
                          {m.text}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* INPUT */}
            <div className="shrink-0 border-t px-4 py-3 flex gap-2">
              <Input
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                placeholder="Type a message..."
                className="flex-1"
              />

              <Button onClick={sendMessage} disabled={conversationLoading}>
                Send
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* NEW MESSAGE DIALOG */}
    <Dialog open={showNewMessageDialog} onOpenChange={setShowNewMessageDialog}>
      <DialogContent className="max-w-sm">
        <DialogTitle>Select a contact</DialogTitle>

        <div className="space-y-2 mt-3">
          {connectedUsers.map((u: any) => (
            <div
              key={u.id}
              onClick={() => {
                setShowNewMessageDialog(false);
                openConversation(u.id);
              }}
              className="flex items-center gap-3 p-3 rounded cursor-pointer hover:bg-muted"
            >
              <img
                src={u.image || u.profilePic || "/placeholder-avatar.jpg"}
                className="w-10 h-10 rounded-full object-cover"
              />
              <p className="font-medium">
                {u.fname} {u.lname}
              </p>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  </div>
  );

}
